import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, X, FileText, Video, Image as ImageIcon, File, Trash2, Camera } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const schema = z.object({
  title: z.string().trim().min(2, "عنوان الدرس مطلوب").max(200),
  content: z.string().trim().max(5000).optional(),
  class_id: z.string().optional(),
  scheduled_at: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface LessonData {
  id: string;
  title: string;
  content: string | null;
  class_id: string | null;
  scheduled_at: string | null;
  media_url?: string | null;
}

interface Props {
  lesson: LessonData;
  classes: { id: string; name: string }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES: Record<string, string> = {
  "application/pdf": "pdf",
  "application/msword": "word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
  "video/mp4": "video",
  "video/webm": "video",
  "video/quicktime": "video",
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
};

const fileTypeIcon = (type: string) => {
  switch (type) {
    case "pdf": case "word": return <FileText className="w-4 h-4" />;
    case "video": return <Video className="w-4 h-4" />;
    case "image": return <ImageIcon className="w-4 h-4" />;
    default: return <File className="w-4 h-4" />;
  }
};

const fileTypeLabel = (type: string) => {
  switch (type) {
    case "pdf": return "PDF";
    case "word": return "Word";
    case "video": return "فيديو";
    case "image": return "صورة";
    default: return "ملف";
  }
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function EditLessonDialog({ lesson, classes, open, onOpenChange, onSuccess }: Props) {
  const [newFiles, setNewFiles] = useState<{ file: File; type: string }[]>([]);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [removeCover, setRemoveCover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Load existing files
  const { data: existingFiles, refetch: refetchFiles } = useQuery({
    queryKey: ["edit-lesson-files", lesson.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_files")
        .select("*")
        .eq("lesson_id", lesson.id)
        .order("created_at", { ascending: true });
      return data || [];
    },
    enabled: open,
  });

  // Reset form when lesson changes
  useEffect(() => {
    if (open) {
      reset({
        title: lesson.title,
        content: lesson.content || "",
        class_id: lesson.class_id || undefined,
        scheduled_at: lesson.scheduled_at ? lesson.scheduled_at.slice(0, 16) : "",
      });
      setNewFiles([]);
      setCoverImage(null);
      setCoverPreview(lesson.media_url || null);
      setRemoveCover(false);
    }
  }, [open, lesson, reset]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid: { file: File; type: string }[] = [];
    for (const f of selected) {
      if (f.size > MAX_FILE_SIZE) {
        toast({ title: "خطأ", description: `الملف "${f.name}" أكبر من 20MB`, variant: "destructive" });
        continue;
      }
      const type = ACCEPTED_TYPES[f.type];
      if (!type) {
        toast({ title: "خطأ", description: `نوع الملف "${f.name}" غير مدعوم`, variant: "destructive" });
        continue;
      }
      valid.push({ file: f, type });
    }
    setNewFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteExistingFile = async (fileId: string, fileUrl: string) => {
    setDeletingFileId(fileId);
    try {
      // Extract storage path from URL
      const urlParts = fileUrl.split("/lesson-files/");
      if (urlParts[1]) {
        await supabase.storage.from("lesson-files").remove([decodeURIComponent(urlParts[1])]);
      }
      const { error } = await supabase.from("lesson_files").delete().eq("id", fileId);
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "تم حذف الملف" });
        refetchFiles();
      }
    } finally {
      setDeletingFileId(null);
    }
  };

  const uploadNewFiles = async (lessonId: string, nurseryId: string) => {
    let count = 0;
    for (const { file, type } of newFiles) {
      const ext = file.name.split(".").pop() || "bin";
      const path = `${nurseryId}/${lessonId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("lesson-files").upload(path, file);
      if (error) { console.error("Upload error:", error); continue; }
      await supabase.from("lesson_files").insert({
        lesson_id: lessonId,
        nursery_id: nurseryId,
        file_name: file.name,
        file_url: path,
        file_type: type,
        file_size: file.size,
      });
      count++;
    }
    return count;
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: "خطأ", description: "حجم الصورة أكبر من 5MB", variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "خطأ", description: "الملف ليس صورة", variant: "destructive" });
      return;
    }
    setCoverImage(file);
    setCoverPreview(URL.createObjectURL(file));
    setRemoveCover(false);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const uploadCoverImage = async (nurseryId: string): Promise<string | null> => {
    if (!coverImage) return null;
    const ext = coverImage.name.split(".").pop() || "jpg";
    const path = `${nurseryId}/covers/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("lesson-files").upload(path, coverImage);
    if (error) { console.error("Cover upload error:", error); return null; }
    return path;
  };

  const onSubmit = async (data: FormData) => {
    if (!profile?.nursery_id) return;
    setUploading(true);
    try {
      let mediaUrl: string | null | undefined = undefined;
      if (coverImage) {
        mediaUrl = await uploadCoverImage(profile.nursery_id);
      } else if (removeCover) {
        mediaUrl = null;
      }

      const updateData: any = {
        title: data.title,
        content: data.content || null,
        class_id: data.class_id || null,
        scheduled_at: data.scheduled_at || null,
      };
      if (mediaUrl !== undefined) updateData.media_url = mediaUrl;

      const { error } = await supabase.from("lessons").update(updateData).eq("id", lesson.id);

      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
        return;
      }

      if (newFiles.length > 0) {
        const count = await uploadNewFiles(lesson.id, profile.nursery_id);
        if (count < newFiles.length) {
          toast({ title: "تنبيه", description: `تم رفع ${count} من ${newFiles.length} ملفات`, variant: "destructive" });
        }
      }

      toast({ title: "تم تحديث الدرس بنجاح" });
      queryClient.invalidateQueries({ queryKey: ["lesson-files"] });
      queryClient.invalidateQueries({ queryKey: ["lesson-file-counts"] });
      setNewFiles([]);
      onOpenChange(false);
      onSuccess();
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل الدرس</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>عنوان الدرس *</Label>
            <Input {...register("title")} />
            {errors.title && <p className="text-destructive text-xs mt-1">{errors.title.message}</p>}
          </div>
          <div>
            <Label>المحتوى</Label>
            <Textarea {...register("content")} rows={3} />
          </div>
          <div>
            <Label>الفصل</Label>
            <Select defaultValue={lesson.class_id || undefined} onValueChange={(v) => setValue("class_id", v)}>
              <SelectTrigger><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>التاريخ والوقت</Label>
            <Input type="datetime-local" {...register("scheduled_at")} />
          </div>

          {/* Cover Image */}
          <div className="space-y-2">
            <Label>صورة الدرس</Label>
            {coverPreview && !removeCover ? (
              <div className="relative">
                <img src={coverPreview} alt="غلاف الدرس" className="w-full h-40 object-cover rounded-lg" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 left-2 h-7 w-7 p-0"
                  onClick={() => { setCoverImage(null); setCoverPreview(null); setRemoveCover(true); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => coverInputRef.current?.click()}
              >
                <Camera className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">اضغط لإضافة صورة غلاف</p>
              </div>
            )}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleCoverSelect}
            />
          </div>

          {/* Existing Files */}
          {existingFiles && existingFiles.length > 0 && (
            <div className="space-y-2">
              <Label>المرفقات الحالية</Label>
              {existingFiles.map((f: any) => (
                <div key={f.id} className="flex items-center gap-2 bg-muted/50 rounded-md p-2 text-sm">
                  {fileTypeIcon(f.file_type)}
                  <span className="flex-1 truncate">{f.file_name}</span>
                  <Badge variant="secondary" className="text-xs">{fileTypeLabel(f.file_type)}</Badge>
                  {f.file_size && <span className="text-xs text-muted-foreground">{formatSize(f.file_size)}</span>}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={deletingFileId === f.id}
                    onClick={() => deleteExistingFile(f.id, f.file_url)}
                  >
                    {deletingFileId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* New File Upload */}
          <div className="space-y-2">
            <Label>إضافة مرفقات جديدة</Label>
            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">اضغط لرفع ملفات</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, Word, فيديو, صور — حتى 20MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.mp4,.webm,.mov,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileSelect}
            />
            {newFiles.length > 0 && (
              <div className="space-y-2 mt-2">
                {newFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-muted/50 rounded-md p-2 text-sm">
                    {fileTypeIcon(f.type)}
                    <span className="flex-1 truncate">{f.file.name}</span>
                    <Badge variant="secondary" className="text-xs">{fileTypeLabel(f.type)}</Badge>
                    <span className="text-xs text-muted-foreground">{formatSize(f.file.size)}</span>
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeNewFile(i)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting || uploading}>
            {(isSubmitting || uploading) ? (
              <><Loader2 className="w-4 h-4 animate-spin ml-2" />{uploading ? "جاري الرفع..." : "جاري الحفظ..."}</>
            ) : (
              "حفظ التعديلات"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
